import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { loginUser } from '../../shared/api/api';
import { startSession } from '../../shared/auth/session';
import { isConfigured, passwordLoginEnabled } from '../../shared/config/authConfig';
import './LoginPage.css';

const NUM_COLS = 44;
const HEX_COLS = Array.from({ length: NUM_COLS }, (_, i) => i);
const HEX_ROWS = Array.from({ length: 20 });

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = location.state?.sessionExpired;
  const expiredReason = location.state?.reason;

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const auth = useAuth();
  const ssoAvailable = isConfigured();
  const showPasswordForm = passwordLoginEnabled();

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleMicrosoftSignIn = () => {
    setError('');
    setLoading(true);
    auth.signinRedirect();
  };

  const validate = () => {
    const errors = { username: '', password: '' };
    if (!username.trim()) errors.username = 'Username is required.';
    if (!password)         errors.password = 'Password is required.';
    setFieldErrors(errors);
    return !errors.username && !errors.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);

    try {
      const response = await loginUser(username, password);
      const { token, expiresIn, username: user } = response.data;

      startSession({ token, username: user, expiresIn });

      navigate('/dashboard');
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        setError('The username or password you entered is incorrect. Please try again.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">

      {/* Full-screen honeycomb hex field */}
      <div className="login-hex-field" aria-hidden="true">
        {HEX_COLS.map((col) => (
          <div
            key={col}
            className={[
              'login-hex-col-field',
              `login-hex-col-v${col % 5}`,
              col % 2 === 1 ? 'login-hex-col--offset' : '',
            ].join(' ')}
          >
            {HEX_ROWS.map((_, i) => (
              <div key={i} className="login-hex-grid-cell" />
            ))}
          </div>
        ))}
      </div>

      {/* Glow highlights */}
      <div className="login-bg-highlight login-bg-highlight--1" aria-hidden="true" />
      <div className="login-bg-highlight login-bg-highlight--2" aria-hidden="true" />
      <div className="login-bg-highlight login-bg-highlight--3" aria-hidden="true" />

      <div className={`login-card${mounted ? ' login-card--visible' : ''}`}>

        {/* Brand header */}
        <div className="login-header">
          <img src="/command_quality_pty_ltd_logo.jpg" alt="Command Quality" className="login-logo" />
          <h1 className="login-title">Developer Tool Platform</h1>
          <p className="login-subtitle">Sign in to your account</p>
        </div>

        {/* Session expired banner */}
        {sessionExpired && (
          <div className="login-session-expired">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>{expiredReason || 'You have been logged out due to inactivity for safety purposes. Please sign in again.'}</span>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        {ssoAvailable && (
          <button
            type="button"
            className="btn btn-primary login-submit login-microsoft-btn"
            onClick={handleMicrosoftSignIn}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="login-btn-spinner" aria-hidden="true" />
                <span>Logging you in…</span>
              </>
            ) : (
              <>
                <svg className="login-ms-icon" viewBox="0 0 21 21" width="18" height="18" aria-hidden="true">
                  <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                  <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                <span>Sign in with Microsoft</span>
              </>
            )}
          </button>
        )}

        {ssoAvailable && showPasswordForm && (
          <div className="login-divider"><span>or</span></div>
        )}

        {showPasswordForm && (
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setFieldErrors((p) => ({ ...p, username: '' })); }}
                placeholder="Enter your username"
                autoComplete="username"
                className={fieldErrors.username ? 'input-error' : ''}
              />
              {fieldErrors.username && <span className="field-error">{fieldErrors.username}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="login-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFieldErrors((p) => ({ ...p, password: '' })); }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className={fieldErrors.password ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
            </div>

            <button type="submit" className="btn btn-primary login-submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}

export default LoginPage;
